<script setup lang="ts">
  /**
   * Rows-per-page control — 03-components/12b-data-table, the `.pagination__page-size` block.
   *
   * A dropdown, not a form field: no `<label for>`, no validation and no 44px control. It is the
   * pager's own compact control, at `.pagination__button`'s 32px, sitting beside the count. It
   * composes BfSelect's combobox interaction — click/Enter/Space open, arrow keys move the
   * highlight, Escape and click-outside close — rather than its markup, because a page-size list
   * never needs a search field, trailing labels or blocked rows.
   *
   * The menu is fixed-positioned and teleported to `<body>`: the table frame this sits inside
   * carries `overflow-x: auto`, which forces `overflow-y` to `auto` as well the moment the frame
   * scrolls, and a menu that stayed a normal child would be clipped there instead of overlapping
   * the table the way a menu should. Its coordinates are computed once, at open, so a scroll
   * outside the menu closes it — a scroll *inside* it does not, since a long list scrolls itself
   * — while a resize places it again rather than closing: the control has not gone, only its
   * coordinates are stale.
   *
   * Being fixed is also why `placeMenu` has to fit the menu to the viewport rather than just
   * hang it off the control: a fixed menu that runs past the bottom edge cannot be scrolled to,
   * and the pager is the last thing on the page, so on a short window the control sits low and
   * every size but the first was unreachable. It drops down when the list fits under the
   * control, flips above it when it does not, and scrolls itself when neither side is tall
   * enough.
   */
  import { computed, nextTick, onBeforeUnmount, ref, watch } from 'vue'

  import BfIconChevronDown from '@/components/ui/icons/BfIconChevronDown.vue'
  import { BfPageSizeOption } from '@/typescript/types/ui-components/BfPaginationI'

  const props = withDefaults(
    defineProps<{
      /** The page size currently in force. Compared to option values as a string. */
      modelValue: number | string
      options: BfPageSizeOption[]
      /** Id of the `.pagination__page-size-label` that names this control. */
      labelledBy?: string
      disabled?: boolean
    }>(),
    { labelledBy: undefined, disabled: false },
  )

  const emit = defineEmits<{ 'update:modelValue': [value: number | string] }>()

  const MENU_GAP = 4
  const VIEWPORT_MARGIN = 8
  const MENU_MAX_HEIGHT = 240
  const MENU_MIN_HEIGHT = 88
  const OPTION_HEIGHT = 40
  const MENU_CHROME = 10

  const open = ref(false)
  const activeIndex = ref(-1)
  const controlRef = ref<HTMLButtonElement | null>(null)
  const rootRef = ref<HTMLElement | null>(null)
  const menuRef = ref<HTMLElement | null>(null)
  const menuStyle = ref<Record<string, string>>({})

  const sameValue = (a: number | string, b: number | string): boolean => String(a) === String(b)

  const indexOfValue = computed<number>(() =>
    props.options.findIndex(option => sameValue(option.value, props.modelValue)),
  )

  const displayLabel = computed<string>(() => props.options[indexOfValue.value]?.label ?? String(props.modelValue))

  const placeMenu = (): void => {
    const rect = controlRef.value?.getBoundingClientRect()
    if (!rect) return

    const spaceBelow = window.innerHeight - rect.bottom - MENU_GAP - VIEWPORT_MARGIN
    const spaceAbove = rect.top - MENU_GAP - VIEWPORT_MARGIN
    // `scrollHeight` stops inside the border, while the border-box `max-height` it feeds counts it:
    // the menu came out 2px short of its own list and grew a scrollbar with every size in view.
    const menu = menuRef.value
    const naturalHeight = menu
      ? menu.scrollHeight + menu.offsetHeight - menu.clientHeight
      : props.options.length * OPTION_HEIGHT + MENU_CHROME
    const wantedHeight = Math.min(naturalHeight, MENU_MAX_HEIGHT)

    const dropsDown = spaceBelow >= wantedHeight || spaceBelow >= spaceAbove
    const available = Math.max(dropsDown ? spaceBelow : spaceAbove, MENU_MIN_HEIGHT)
    const height = Math.min(wantedHeight, available)

    // At least trigger-width, as Select's (16) menu is — never exactly it: the control is sized by
    // the value in force, so a narrow "10" would otherwise cut "100" off and scroll it sideways.
    const menuWidth = Math.max(rect.width, menuRef.value?.offsetWidth ?? 0)
    const left = Math.max(VIEWPORT_MARGIN, Math.min(rect.left, window.innerWidth - VIEWPORT_MARGIN - menuWidth))

    menuStyle.value = {
      left: `${left}px`,
      minWidth: `${rect.width}px`,
      maxHeight: `${height}px`,
      top: dropsDown ? `${rect.bottom + MENU_GAP}px` : `${Math.max(VIEWPORT_MARGIN, rect.top - MENU_GAP - height)}px`,
    }
  }

  const close = (): void => {
    open.value = false
    activeIndex.value = -1
  }

  const toggle = (): void => {
    if (props.disabled) return
    if (open.value) {
      close()
      return
    }

    activeIndex.value = Math.max(0, indexOfValue.value)
    placeMenu()
    open.value = true
    void nextTick(placeMenu)
  }

  const select = (option: BfPageSizeOption): void => {
    close()
    if (!sameValue(option.value, props.modelValue)) emit('update:modelValue', option.value)
  }

  const moveActive = (step: number): void => {
    const count = props.options.length
    if (!count) return

    activeIndex.value = (activeIndex.value + step + count) % count
  }

  const onKeydown = (event: KeyboardEvent): void => {
    switch (event.key) {
      case 'Enter':
      case ' ':
        event.preventDefault()
        if (open.value && activeIndex.value >= 0) select(props.options[activeIndex.value])
        else toggle()
        break
      case 'Escape':
        if (!open.value) break
        event.preventDefault()
        close()
        break
      case 'ArrowDown':
        event.preventDefault()
        if (open.value) moveActive(1)
        else toggle()
        break
      case 'ArrowUp':
        if (!open.value) break
        event.preventDefault()
        moveActive(-1)
        break
    }
  }

  const onDocumentClick = (event: MouseEvent): void => {
    const target = event.target as Node | null
    if (target && !rootRef.value?.contains(target)) close()
  }

  /**
   * A scroll of the menu's own list is not a scroll away from the control — and the list does
   * scroll, whenever a host offers more sizes than the menu's max height fits. `classList` is
   * reached optionally because a scroll of the page reports the document as its target, and a
   * document has none.
   */
  const onScroll = (event: Event): void => {
    if ((event.target as HTMLElement | null)?.classList?.contains('select-field__menu')) return

    close()
  }

  /** Listeners live only while the menu is open — a closed control watches nothing. */
  watch(open, isOpen => {
    if (isOpen) {
      document.addEventListener('click', onDocumentClick)
      window.addEventListener('scroll', onScroll, true)
      window.addEventListener('resize', placeMenu)
      return
    }

    document.removeEventListener('click', onDocumentClick)
    window.removeEventListener('scroll', onScroll, true)
    window.removeEventListener('resize', placeMenu)
  })

  onBeforeUnmount(() => {
    document.removeEventListener('click', onDocumentClick)
    window.removeEventListener('scroll', onScroll, true)
    window.removeEventListener('resize', placeMenu)
  })
</script>

<template>
  <span ref="rootRef" class="pagination__page-size-field">
    <button
      ref="controlRef"
      type="button"
      class="pagination__page-size-control"
      aria-haspopup="listbox"
      :aria-expanded="open"
      :aria-labelledby="labelledBy"
      :disabled="disabled"
      @click="toggle"
      @keydown="onKeydown"
    >
      <span class="select-field__value">{{ displayLabel }}</span>
    </button>

    <span class="pagination__page-size-caret" :class="{ 'pagination__page-size-caret--open': open }" aria-hidden="true">
      <BfIconChevronDown :size="10" />
    </span>

    <Teleport to="body">
      <ul v-if="open" ref="menuRef" class="select-field__menu" role="listbox" :style="menuStyle">
        <li
          v-for="(option, index) in options"
          :key="option.value"
          class="select-field__option"
          :class="{
            'select-field__option--active': index === activeIndex,
            'select-field__option--selected': sameValue(option.value, modelValue),
          }"
          role="option"
          :aria-selected="sameValue(option.value, modelValue)"
          @click="select(option)"
          @mouseenter="activeIndex = index"
        >
          {{ option.label }}
        </li>
      </ul>
    </Teleport>
  </span>
</template>

<style scoped lang="scss">
  .pagination__page-size-field {
    position: relative;
  }

  .pagination__page-size-control {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    height: 32px;
    padding: 0 26px 0 10px;
    border: 1px solid var(--bf-line);
    border-radius: var(--bf-r-md);
    background: var(--bf-white);
    font-family: var(--bf-font-display);
    font-size: var(--bf-fs-xs_5);
    font-weight: var(--bf-fw-regular);
    letter-spacing: 0.01em;
    color: var(--bf-fg-1);
    font-variant-numeric: tabular-nums;
    cursor: pointer;
    transition:
      border-color var(--bf-dur-fast) var(--bf-ease),
      box-shadow var(--bf-dur-fast) var(--bf-ease);
  }

  .pagination__page-size-control:hover:not(:disabled) {
    border-color: var(--bf-dusky-1);
  }

  /* `aria-expanded="true"` is the open combobox, which keeps its ring while the pointer is in
     the list — the same pairing BfSelect's control uses. */
  .pagination__page-size-control:focus-visible,
  .pagination__page-size-control[aria-expanded='true'] {
    outline: none;
    border-color: var(--bf-form-control-hairline-focus);
    box-shadow: var(--bf-form-focus-ring);
  }

  .pagination__page-size-control:disabled {
    opacity: var(--bf-form-disabled-opacity);
    cursor: not-allowed;
  }

  .pagination__page-size-caret {
    position: absolute;
    top: 50%;
    right: 9px;
    display: inline-flex;
    color: var(--bf-dusky-1);
    pointer-events: none;
    transform: translateY(-50%);
    transition: transform var(--bf-dur-base) var(--bf-ease);
  }

  .pagination__page-size-caret--open {
    transform: translateY(-50%) rotate(180deg);
  }

  /* The listbox shell and its rows, as BfSelect draws them. Fixed rather than absolute because
     the menu is teleported out of the table frame that would otherwise clip it. */
  .select-field__menu {
    position: fixed;
    z-index: 1100;
    width: max-content;
    max-height: 240px;
    margin: 0;
    padding: 4px;
    overflow-x: hidden;
    overflow-y: auto;
    overscroll-behavior: contain;
    list-style: none;
    background: var(--bf-white);
    border: 1px solid var(--bf-line);
    border-radius: var(--bf-r-lg);
    box-shadow: var(--bf-shadow-2);
  }

  .select-field__option {
    padding: 10px 12px;
    font-family: var(--bf-font-body);
    font-size: 15px;
    color: var(--bf-fg-1);
    border-radius: var(--bf-r-md);
    white-space: nowrap;
    font-variant-numeric: tabular-nums;
    cursor: pointer;
  }

  .select-field__option--active {
    background: var(--bf-bg-soft);
  }

  .select-field__option--selected {
    color: var(--bf-orange-dark);
    font-weight: var(--bf-fw-semibold);
  }
</style>
